import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import QRCode from 'qrcode';
import { useUser } from '../../../contexts/UserContext';
import { useNetwork } from '../../../utils/network';
import { useShowSnackbar } from '../../../utils/snackbar';
import {
  PT,
  dialogPaperSx,
  primaryBtnSx,
  ghostBtnSx,
  fieldSx,
  kickerSx
} from '../../../components/portalUi';
import { RecoveryCodesPanel } from './RecoveryCodesPanel';

type TwoFactorAuthModalProps = {
  isTwoFAModalOpen: boolean;
  handleTwoFAModalClose: () => void;
};

export const TwoFactorAuthModal = ({
  isTwoFAModalOpen,
  handleTwoFAModalClose
}: TwoFactorAuthModalProps) => {
  const { user, setUser } = useUser();
  const { get, post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const [qrcodeUrl, setQrCodeUrl] = useState('');
  const [base32Code, setBase32Code] = useState('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [copied, setCopied] = useState(false);
  // The first set of recovery codes, from the verify response — shown once, here.
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  useEffect(() => {
    generateTwoFactorAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateTwoFactorAuth = async () => {
    try {
      const result = await get('/user/otp/generate', true);
      QRCode.toDataURL(result.twoFactorAuthUrl).then(setQrCodeUrl);
      setBase32Code(result.base32);
    } catch (error: any) {
      showSnackbar(error.response.data.message, 'error');
    }
  };

  const verifyTwoFactorAuthSubmit = async () => {
    try {
      const result = await post('/user/otp/verify', { token: verificationCode }, true);
      setUser({ ...user, hasTwoFactorAuthEnabled: true });
      // 2FA is on. Its recovery codes come back with this answer and nowhere else, so
      // the modal stays open on them until the member has saved them (Done).
      if (Array.isArray(result.recoveryCodes) && result.recoveryCodes.length) {
        setRecoveryCodes(result.recoveryCodes.map(String));
      } else {
        handleTwoFAModalClose();
      }
      showSnackbar(result.message, 'success');
    } catch (error: any) {
      showSnackbar(error.response.data.message, 'error');
    }
  };

  const copyKey = async () => {
    try {
      await navigator.clipboard.writeText(base32Code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked; the key is visible to copy manually */
    }
  };

  const steps = [
    'Install Google Authenticator (iOS / Android) or Authy (iOS / Android).',
    'In the authenticator app, tap the "+" icon.',
    'Choose "Scan a barcode (or QR code)" and scan the code below.'
  ];

  return (
    <Dialog
      open={isTwoFAModalOpen}
      // The codes screen has one way out, Done: no backdrop click, no Escape.
      onClose={recoveryCodes ? undefined : handleTwoFAModalClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      PaperProps={{ sx: dialogPaperSx }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 3,
          py: 2.25,
          borderBottom: `1px solid ${PT.border}`
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            display: 'grid',
            placeItems: 'center',
            color: PT.teal,
            background: PT.tealSoft,
            border: `1px solid ${PT.tealBorder}`,
            flexShrink: 0
          }}
        >
          <ShieldRoundedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={kickerSx}>{recoveryCodes ? '2FA is on' : 'Security'}</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: PT.text, lineHeight: 1.2 }}>
            {recoveryCodes ? 'Save your recovery codes' : 'Two-Factor Authentication'}
          </Typography>
        </Box>
        {!recoveryCodes && (
          <IconButton
            onClick={handleTwoFAModalClose}
            aria-label="Close"
            sx={{ color: PT.textMuted, '&:hover': { color: PT.text } }}
          >
            <CloseRoundedIcon />
          </IconButton>
        )}
      </Box>

      {recoveryCodes ? (
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <RecoveryCodesPanel codes={recoveryCodes} onDone={handleTwoFAModalClose} />
        </DialogContent>
      ) : (
        <>
          <DialogContent sx={{ px: 3, py: 2.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: PT.text, mb: 1.5 }}>
              Configure Google Authenticator or Authy
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3 }}>
              {steps.map((s, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      flexShrink: 0,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      font: '800 12px Montserrat, sans-serif',
                      color: PT.teal,
                      background: PT.tealSoft,
                      border: `1px solid ${PT.tealBorder}`
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Typography sx={{ fontSize: 14, color: PT.textMuted, lineHeight: 1.5 }}>{s}</Typography>
                </Box>
              ))}
            </Box>

            {/* QR — kept on a white card so cameras can read it */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '16px',
                  background: '#fff',
                  border: `1px solid ${PT.border}`,
                  boxShadow: '0 8px 26px rgba(0,0,0,0.35)',
                  width: { xs: 168, sm: 208 },
                  height: { xs: 168, sm: 208 },
                  display: 'grid',
                  placeItems: 'center'
                }}
              >
                {qrcodeUrl ? (
                  <Box
                    component="img"
                    src={qrcodeUrl}
                    alt="Two-factor QR code"
                    sx={{ width: '100%', height: '100%' }}
                  />
                ) : (
                  <Typography sx={{ color: '#888', fontSize: 13 }}>Generating…</Typography>
                )}
              </Box>
            </Box>

            {/* Manual secret key */}
            <Typography sx={{ textAlign: 'center', fontSize: 13.5, color: PT.textMuted, mb: 1 }}>
              Or enter this key into your app manually
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: '12px',
                border: `1px solid ${PT.border}`,
                background: 'rgba(255,255,255,0.03)'
              }}
            >
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    font: '700 10px Montserrat, sans-serif',
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: PT.textMuted
                  }}
                >
                  Secret key (Base32)
                </Typography>
                {/* aria-label + data-track hand the analytics click capture (utils/track.ts)
                    a safe label: a triple-click to select the key counts there as a
                    rage-click, and with nothing labelled above it the capture recorded
                    the key's own text — the TOTP secret — in the events table. */}
                <Typography
                  aria-label="Secret key"
                  data-track="security.totp_secret"
                  sx={{
                    mt: 0.3,
                    font: '700 14px ui-monospace, Menlo, Consolas, monospace',
                    color: PT.tealText,
                    wordBreak: 'break-all',
                    lineHeight: 1.4
                  }}
                >
                  {base32Code || '…'}
                </Typography>
              </Box>
              <Tooltip title={copied ? 'Copied' : 'Copy'} placement="top" arrow>
                <IconButton
                  onClick={copyKey}
                  aria-label="Copy secret key"
                  sx={{
                    color: copied ? PT.green : PT.teal,
                    border: `1px solid ${PT.border}`,
                    borderRadius: '10px',
                    flexShrink: 0,
                    '&:hover': { borderColor: PT.tealBorder, background: 'rgba(56,232,255,0.08)' }
                  }}
                >
                  {copied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
                </IconButton>
              </Tooltip>
            </Box>

            {/* Verify */}
            <Box
              sx={{
                mt: 3,
                pt: 3,
                borderTop: `1px solid ${PT.border}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Typography sx={{ fontSize: 14, color: PT.text, mb: 1.5, textAlign: 'center' }}>
                Enter the 6-digit code from your authenticator app
              </Typography>
              <TextField
                id="authAppCode"
                placeholder="000000"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.currentTarget.value)}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 6,
                  style: {
                    textAlign: 'center',
                    letterSpacing: '0.5em',
                    fontSize: 22,
                    fontWeight: 700
                  }
                }}
                sx={{ ...fieldSx, width: 220 }}
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
            <Button onClick={handleTwoFAModalClose} sx={{ ...ghostBtnSx, px: 2.5, py: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={verifyTwoFactorAuthSubmit}
              disabled={verificationCode.length < 6}
              sx={{ ...primaryBtnSx, px: 3, py: 1 }}
            >
              Verify &amp; Activate
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
